import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useRef, useState } from "react";
import VoiceEMRRecorder from "@/app/components/routes/auth_routes/EMRTemplateCreationPage/VoiceEMRRecorder";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useEMRListAPI } from "@/app/hooks/api_hooks/emr/useEMRListAPI";
import { useListEMRUserAndPetAPI } from "@/app/hooks/api_hooks/emr/useListEMRUserAndPetAPI";
import { useLoader } from "@/app/hooks/useLoader";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { FaNotesMedical, FaRegFilePdf } from "react-icons/fa6";
import EMRTabHeader from "./EMRTabHeader";

import { IEMRListForUserAndPetResponseData } from "@/api_framework/api_modals/EMRs";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setEMRMode, setEMROverDraft, setLatestEMRDetails } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import clsx from "clsx";
import { format, fromUnixTime } from "date-fns";
import { isArray, isObject, isString, pick, startCase } from "lodash";
import { GrDocumentText } from "react-icons/gr";
import { useNavigate, useParams } from "react-router";
import EMRMenu from "./EMRMenu";
import { useEMRDeleteAPI } from "@/app/hooks/api_hooks/emr/useEMRDeleteAPI";
import { UserIcon } from "@heroicons/react/24/outline";
import { BiNotepad, BiSolidDonateHeart } from "react-icons/bi";
import { FaStethoscope, FaTemperatureHigh, FaVirusCovid } from "react-icons/fa6";
import { HiOutlineBeaker } from "react-icons/hi";
import { IoPlayForwardCircle } from "react-icons/io5";
import { MdOutlineMonitorHeart, MdWorkHistory } from "react-icons/md";
import { PiFishBold, PiPillFill } from "react-icons/pi";
import { RiChatFollowUpLine } from "react-icons/ri";
import RBACFlag from "@/app/components/core/RBACFlag";
import MedicineHistoryTab from "../MedicineHistoryTab";
import AISummaryTab from "../AISummaryTab";
import EMRList from "@components/routes/auth_routes/EMRTemplateCreationPage/EMRList";
import { useV2EMRListAPI } from "@/app/hooks/api_hooks/emr/useV2EMRListAPI";
import { useEMRV2DeleteAPI } from "@/app/hooks/api_hooks/emr/useEMRV2DeleteAPI";
import { EMRCreationSectionsKeys } from "@/app/routes/auth_routes/EMRCreationPage/interface";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useVoiceSession, MAX_CONCURRENT_VOICE_SESSIONS } from "@/app/context/VoiceSessionContext";
import { useCreateEMRAPI } from "@/app/hooks/api_hooks/emr/useCreateEMRAPI";
import {
  findRecentlyCompletedEMRForPet,
  isActiveEMRForPet,
  isVoiceAutoStartUser,
  petHasAttendingVoiceBooking,
  petHasVoiceAutoCreateBooking,
  PWL_VOICE_INFLIGHT,
  voiceHandoffKey,
  VOICE_PARAM_START,
  VOICE_PARAM_VERIFY,
} from "@/app/utils/voiceAutoStart";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import { ROUTES } from "@/app/routes";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useVoiceEMRClickLog } from "@/app/hooks/useVoiceEMRClickLog";

export const EMR_SECTION_ICONS: { [key in EMRCreationSectionsKeys]: React.ReactNode } = {
  customer_profile_section: <UserIcon />,
  vitals_section: <FaTemperatureHigh />,
  diagnosis_section: <MdOutlineMonitorHeart />,
  food_and_acc_section: <PiFishBold />,
  history_section: <MdWorkHistory />,
  lab_test_section: <HiOutlineBeaker />,
  medication_section: <PiPillFill />,
  next_steps_section: <IoPlayForwardCircle />,
  prognosis_section: <BiSolidDonateHeart />,
  symptoms_section: <FaVirusCovid />,
  treatment_section: <FaStethoscope />,
  follow_up_section_section: <RiChatFollowUpLine />,
  medical_instructions_section: <FaNotesMedical />,
  subjective_history_section: <BiNotepad />,
  emr_images: <FaNotesMedical />,
};

const RenderTimelineContent: React.FC<{
  emrSectionKey: EMRCreationSectionsKeys;
  emr: IEMRListForUserAndPetResponseData;
}> = ({ emr, emrSectionKey }) => {

  const { hasAnyValidValue } = useUtils();
  const excludedKeys = [
    "created_at",
    "created_by",
    "emr_id",
    "visited_clinic_id",
    "updated_at",
    "updated_by",
    "staff_id",
    "emr_status",
    "customer_profile_section",
    "refer_clinic_id",
    "refer_staff_id",
    "refer_city_id",
  ];

  const historySectionSortedKeysOrder = ["deworming", "vaccination", "food_pref", "no_of_meals", "food_brands"];

  const excludedSubKeys = ["product_id", "medicine_id"];

  const excludedSubHeadingKeysSections = [
    "subjective_history_section",
    "diagnosis_section",
    "symptoms_section",
    "prognosis_section",
    "medical_instructions_section",
  ];

  const keysWithValidValue = hasAnyValidValue(emr[emrSectionKey]);
  const shouldShowSection = keysWithValidValue?.length > 0;

  /**
   * Renders the content for a specific section of the EMR timeline.
   *
   * @param {EMRCreationSectionsKeys} emrSectionKey - The key representing the section of the EMR.
   * @param {IEMRListForUserAndPetResponseData} emr - The EMR data for the user and pet.
   * @returns {JSX.Element | null} - The rendered content for the section, or null if the section should not be shown.
   *
   * This function takes an EMR section key and the corresponding EMR data, and renders the content for that section.
   * It handles different sections such as "vitals_section", "treatment_section", "food_and_acc_section", and others.
   * The function also filters out excluded keys and sub-keys, and formats the content accordingly.
   */

  const renderSubSectionHeading = (subSectionKey: string) => {
    if (subSectionKey === "diagnosis") {
      return "Assessment";
    }

    return startCase(subSectionKey?.split("_")?.join(" "));
  };

  /**
   * Renders the sub-section heading based on the provided sub-section key.
   *
   * @param {string} subSectionKey - The key representing the sub-section.
   * @returns {string} - The formatted sub-section heading.
   *
   * This function takes a sub-section key and returns a formatted heading for the sub-section.
   * If the sub-section key is "diagnosis", it returns "Assessment". Otherwise, it converts the
   * sub-section key to start case format, replacing underscores with spaces.
   */

  const renderSectionHeading = (section: string) => {
    if (section === "diagnosis_section") {
      return "Assessment";
    }
    if (section === "symptoms_section") {
      return "Physical Examination";
    }
    return startCase(section?.split("_section")?.join(" "));
  };

  /* Sorts the keys of the section based on the order of the keys */

  const getSortedOrderKeysForSection = (emrSection: any, emrSectionKey: EMRCreationSectionsKeys) => {
    if (emrSectionKey === "history_section") {
      return pick(emrSection, historySectionSortedKeysOrder);
    }
    return emrSection;
  };

  const renderKeyValuePairContent = (hKey: string, emrSectionKey: EMRCreationSectionsKeys) => {
    const fieldMap = emr[emrSectionKey][hKey];

    return (
      <div className="">
        {!excludedSubHeadingKeysSections.includes(emrSectionKey) && (
          <span className="font-semibold text-gray-500">{renderSubSectionHeading(hKey)} :</span>
        )}
        <span>
          {isArray(fieldMap) &&
            fieldMap?.map((field: any) =>
              isObject(field) ? (
                <span>{renderField(emrSectionKey, field)}</span>
              ) : (
                <span className="ml-1" key={field}>
                  {field},
                </span>
              ),
            )}
          {isString(emr[emrSectionKey][hKey]) && (
            <span>
              {" "}
              {emr[emrSectionKey][hKey]?.length === 0
                ? "Not Avaialable"
                : emr[emrSectionKey][hKey]?.split("\n")?.map((val) => {
                  return <p>{val}</p>;
                })}
            </span>
          )}
        </span>
      </div>
    );
  };

  const renderField = (section: string, field: any) => {
    if (section === "medication_section") {
      const constructedString = `${field?.name ?? ""} , ${field?.dose ?? ""} ${field?.dose_unit ?? ""} , ${field?.frequency ?? ""
        } , ${field?.timing ?? ""} ,  ${field?.duration ?? ""} ${field?.duration_unit ?? ""},  ${field?.remark ?? ""}`;
      const taperingDoseField = field["tapering_doses"];

      const taperingDoesStringified =
        taperingDoseField?.length > 0
          ? taperingDoseField
            ?.map((taper) => {
              return `${field?.name ?? ""} , ${taper?.dose ?? ""} ${taper?.dose_unit ?? ""} , ${taper?.frequency ?? ""} , ${taper?.timing ?? ""} ,  ${taper?.duration ?? ""} ${taper?.duration_unit ?? ""},  ${taper?.remark ?? ""}`;
            })
            ?.join("\r\n")
          : "";

      const medications = constructedString.concat("\r\n").concat(taperingDoesStringified);
      return (
        <ul className="list-disc">
          {medications?.split("\r").map((medication, index) => {
            if (medication.trim() === "") {
              return null;
            }
            return (
              <li className="ml-4 " key={index}>
                {medication}
              </li>
            );
          })}
        </ul>
      );
    } else if (section === "lab_test_section") {
      const constructedString = `${field?.name ?? ""} ${field?.quantity ? `, ${field?.quantity} QTY` : ""
        } ${field?.remark ? `, ${field?.remark}` : ""}`;

      const foodAndAccString = constructedString.concat("\r\n");
      return (
        <ul className="list-disc">
          {foodAndAccString?.split("\n").map((foodDetails, index) => {
            if (foodDetails.trim() === "") {
              return null;
            }
            return (
              <li className="ml-4 " key={index}>
                {foodDetails}
              </li>
            );
          })}
        </ul>
      );
    } else if (section === "treatment_section") {
      const constructedString = `${field?.name ?? ""} ${field?.quantity ? `, ${field?.quantity} QTY` : ""
        } ${field?.remark ? `, ${field?.remark}` : ""}`;

      const foodAndAccString = constructedString.concat("\r\n");
      return (
        <ul className="list-disc">
          {foodAndAccString?.split("\n").map((foodDetails, index) => {
            if (foodDetails.trim() === "") {
              return null;
            }
            return (
              <li className="ml-4 " key={index}>
                {foodDetails}
              </li>
            );
          })}
        </ul>
      );
    } else if (section === "food_and_acc_section") {
      const constructedString = `${field?.name ?? ""} ${field?.quantity ? `, ${field?.quantity} QTY` : ""
        } ${field?.remark ? `, ${field?.remark}` : ""}`;

      const foodAndAccString = constructedString.concat("\r\n");
      return (
        <ul className="list-disc">
          {foodAndAccString?.split("\n").map((foodDetails, index) => {
            if (foodDetails.trim() === "") {
              return null;
            }
            return (
              <li className="ml-4 " key={index}>
                {foodDetails}
              </li>
            );
          })}
        </ul>
      );
    } else if (section === "diagnosis_section") {
      return field["name"]?.split("\n")?.map((val) => {
        return <p>{val}</p>;
      });
    } else {
      return Object.entries(field)
        ?.map((val) => {
          if (excludedSubKeys.includes(val[0])) {
            return null;
          }
          return `${val[0]}: ${val[1]}`;
        })
        ?.filter((val: any) => val !== null && val.trim() !== "")
        ?.join(" , ");
    }
  };

  const renderContent = () => {
    if (emrSectionKey === "vitals_section") {
      return (
        <div className="mt-2">
          <p className="font-semibold text-theme-xs">Vitals</p>
          <div className="text-theme-xs">
            <span>Weight</span> : <span>{emr.vitals_section?.weight_kg} kg</span>
          </div>
          <div className="text-theme-xs">
            <span>Temperature</span> :{" "}
            <span>
              {emr?.vitals_section?.temperature} {emr?.vitals_section?.temperature_unit}
            </span>
          </div>
          <div className="text-theme-xs">
            <span>Body Score</span> : <span>{emr?.vitals_section?.body_score}</span>
          </div>
          <div className="text-theme-xs">
            <span>Dental Score</span> : <span>{emr?.vitals_section?.dental_score}</span>
          </div>
        </div>
      );
    } else {
      return (
        !excludedKeys.includes(emrSectionKey) &&
        shouldShowSection && (
          <div className="mt-6 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <p className={clsx("font-semibold rounded-md flex justify-start items-center gap-x-2")}>
              {renderSectionHeading(emrSectionKey)}
            </p>
            {Object.keys(getSortedOrderKeysForSection(emr[emrSectionKey], emrSectionKey))?.map((hKey, idx) => {
              return (
                !excludedKeys.includes(hKey) &&
                keysWithValidValue.includes(hKey) && (
                  <div key={`${hKey}:${idx}:${emr?.emr_id}`} className="mt-1">
                    {renderKeyValuePairContent(hKey, emrSectionKey)}
                  </div>
                )
              );
            })}
          </div>
        )
      );
    }
  };
  return renderContent();
};

const EMRTimelineRenderer: React.FC<{ emr: IEMRListForUserAndPetResponseData }> = ({ emr }) => {
  const sortedEMRSections = pick(emr, [
    "customer_profile_section",
    "vitals_section",
    "history_section",
    "subjective_history_section",
    "symptoms_section",
    "diagnosis_section",
    "prognosis_section",
    "lab_test_section",
    "treatment_section",
    "medication_section",
    "food_and_acc_section",
    "medical_instructions_section",
    "follow_up_section_section",
    "next_steps_section",
  ]);

  return (
    <div className="pl-10">
      {Object.keys(sortedEMRSections)?.map((key) => {
        return <RenderTimelineContent key={key} emr={emr} emrSectionKey={key as EMRCreationSectionsKeys} />;
      })}
      {/* emr_images */}
      {emr?.emr_images?.length > 0 && (
        <div className="mt-8">
          <p className="font-semibold text-theme-xs">Other Attachments</p>
          <div className="flex gap-2 mt-3">
            {emr?.emr_images?.map((image, index) => (
              <div key={index} className="flex flex-col ">
                {image?.split(".")?.pop()?.toLowerCase() === "pdf" ? (
                  <img
                    onClick={() => window.open(image, "_blank")}
                    src={"https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/pdf-svgrepo-com.svg"}
                    alt={image}
                    className="w-[50px] h-[50px] object-cover cursor-pointer"
                  />
                ) : (
                  <img
                    src={image}
                    onClick={() => window.open(image, "_blank")}
                    alt={`Uploaded Image ${index + 1}`}
                    className="w-16 h-16 object-cover rounded-md mr-2"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EMRTab = () => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat } = useUtils();
  const deleteEMR = useEMRDeleteAPI();
  const deleteV2EMR = useEMRV2DeleteAPI();
  const { setModalData } = useModalActions("EMR_OVERDRAFT_MODAL");
  const { setVisible: quickViewVisible, setModalData: setQuickViewModalData } = useModalActions("EMR_QUICK_VIEW_MODAL");
  const [draftEMRs, setDRAFTEMRs] = useState<IEMRListForUserAndPetResponseData[]>([]);
  const [_justNewEMRs, setJustNewEMRs] = useState<IEMRListForUserAndPetResponseData[]>([]);
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const isLoading = useLoader("isEMRFetchInProgress");

  const { vmsEMRs: v2EmrListData, execute: fetchV2EmrList } = useV2EMRListAPI();

  const navigate = useNavigate();
  const { petId, customerEMRDetails, petDetails, customerId } = routeState.state as ICustomerProfileState;
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) => state.auth?.user?.email);

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    startSession: startVoiceSession,
    endSession: endVoiceSession,
    sessions: voiceSessions,
    hasSessionForPet: hasVoiceSessionForPet,
  } = useVoiceSession();
  const { execute: createDraftEMR } = useCreateEMRAPI();
  const currentUserEmail = useAppSelector((state) => state.auth?.user?.email);
  const mappedClinicId = useAppSelector((state) => state.root?.UIStates?.mappedClinicId);
  const { execute: clickLogAPI } = useClickLogAPI();
  const fetchCustomerBookings = useUserBookingsListAPI();
  const voiceAutoStartFiredRef = useRef(false);
  // Every `return` in the auto-start effect below was silent, so a hand-off that
  // declined to record left no trace at all.
  const logVoiceEMR = useVoiceEMRClickLog();

  /* The store's petId lags the URL when the same customer is reopened on a
     different pet (the page is only gated on the customer - see
     CustomerDetailsPage), and this effect runs before the parent writes the new
     pet in. The voice flow creates an EMR, so it reads the pet off the URL. */
  const routeParams = useParams();
  const voicePetId = routeParams?.pet_id || petId;
  const voiceCustomerId = routeParams?.user_id || customerId;

  // `?voice=1` (from the PWL patient-name link / Create EMR / Edit EMR) means
  // "start the floating recorder for this pet". It runs here — the tab that owns
  // the mic — so it works whether the doctor stayed in this tab or opened it
  // fresh via right-click → "open in new tab". A same-tab click already claims
  // the start (PWL_VOICE_INFLIGHT) or has a live session, so we skip the duplicate.
  //
  // `?voice=check` is the same thing for a booking the PWL row did NOT show as
  // qualifying. That row comes from a cached/polled list, so before writing the
  // recording off we re-read the booking from the appointments API here: it
  // qualifies when it now reads Attending, or Waiting within the ±10-minute
  // window around its scheduled start → the identical flow below runs; anything
  // else → silent no-op.
  useEffect(() => {
    const voiceParam = searchParams.get("voice");
    const isVoiceStart = voiceParam === VOICE_PARAM_START;
    const isVoiceVerify = voiceParam === VOICE_PARAM_VERIFY;
    if (!isVoiceStart && !isVoiceVerify) return;
    if (!voiceCustomerId || !voicePetId || voiceAutoStartFiredRef.current) return;

    // Strip the flag right away so a refresh can't start a second recording.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("voice");
    setSearchParams(nextParams, { replace: true });

    const logAutoStart = (
      event_type: string,
      cta_text: string,
      extra: Parameters<typeof logVoiceEMR>[2] = {},
    ) =>
      logVoiceEMR(event_type, cta_text, {
        client_id: voiceCustomerId,
        pet_id: voicePetId,
        ...extra,
      });
    const logSkip = (reason: string, cta_text: string, extra: Parameters<typeof logVoiceEMR>[2] = {}) =>
      logAutoStart("VOICE_EMR_AUTOSTART_SKIPPED", cta_text, { event_action: reason, ...extra });

    // Recording only ever starts for the VOICE_AUTO_START_EMAILS rollout, even if
    // a stray/hand-typed `?voice=1` reaches this tab.
    if (!isVoiceAutoStartUser(currentUserEmail)) {
      logSkip("not_rollout_user", "Not on the voice rollout");
      return;
    }

    const key = voiceHandoffKey(voiceCustomerId, voicePetId);
    // Not a failure: the PWL tab already claimed and logged this start.
    if (PWL_VOICE_INFLIGHT.has(key)) return;
    // Already recording this pet — don't create a duplicate session.
    if (hasVoiceSessionForPet(voicePetId)) {
      logSkip("already_recording", "Already recording");
      return;
    }
    // Other patients' recordings can stay parked (paused, not lost) alongside a
    // new one — but only up to the concurrent cap. At the cap, ask the doctor to
    // finish or discard one first rather than opening yet another mic.
    if (voiceSessions.length >= MAX_CONCURRENT_VOICE_SESSIONS) {
      toast.error(
        `You already have ${MAX_CONCURRENT_VOICE_SESSIONS} voice recordings open — finish or discard one before starting another.`,
      );
      logSkip("session_cap", "Session cap reached");
      return;
    }

    voiceAutoStartFiredRef.current = true;
    // Past every synchronous gate — this hand-off is really going to try.
    logAutoStart("VOICE_EMR_AUTOSTART_LAUNCHED", "Auto-start recording", {
      event_action: isVoiceVerify ? "voice_check" : "voice_start",
    });
    PWL_VOICE_INFLIGHT.add(key);

    // Only reuse a draft this same VMS user owns; a draft created by another
    // user is left alone and a fresh EMR is created instead.
    const pickActiveDraftId = (list: unknown): string | undefined => {
      if (!Array.isArray(list)) return undefined;
      return list
        .filter(
          (emr) =>
            isActiveEMRForPet(emr, voicePetId) &&
            !!currentUserEmail &&
            emr?.created_by === currentUserEmail,
        )
        .sort(
          (left, right) =>
            (right?.updated_at ?? right?.created_at ?? 0) -
            (left?.updated_at ?? left?.created_at ?? 0),
        )[0]?.emr_id;
    };

    void (async () => {
      try {
        // The only extra step for `?voice=check`: the live booking status decides.
        // Today's bookings are asked for directly rather than read out of the
        // store, so the answer can't be a leftover from the previous patient.
        if (isVoiceVerify) {
          const bookings = await fetchCustomerBookings(voiceCustomerId, voicePetId, {
            page: 1,
            limit: 50,
            filter_by: "today",
            append: false,
          });
          if (!petHasVoiceAutoCreateBooking(bookings?.data, voicePetId)) {
            logSkip("booking_not_attending", "Booking is not attending");
            return;
          }
        }

        const emrList = await fetchV2EmrList(voiceCustomerId, voicePetId);

        // This visit is already written up: an EMR was completed for the pet
        // within the last few hours. Landing here again from the PWL must not
        // record or open a second EMR for the same consultation — silently, since
        // the doctor didn't ask for anything; "Create EMR" is still there by hand.
        const recentlyCompleted = findRecentlyCompletedEMRForPet(emrList, voicePetId);
        if (recentlyCompleted) {
          logSkip("recently_completed_emr", "Visit already written up", {
            emr_id: recentlyCompleted?.emr_id,
          });
          return;
        }

        let emrId = pickActiveDraftId(emrList);

        if (!emrId) {
          const response = await createDraftEMR(voiceCustomerId, voicePetId, undefined, true);
          emrId = response?.emr_id;

          // Lost the create race (an active draft appeared meanwhile) — re-query.
          if (!emrId) emrId = pickActiveDraftId(await fetchV2EmrList(voiceCustomerId, voicePetId));
        }

        // Returned, not thrown: a throw would re-log this as "error" below.
        if (!emrId) {
          logSkip("no_emr_id", "No EMR to record into");
          toast.error("Couldn't create the EMR.");
          return;
        }

        startVoiceSession({
          emrId,
          userId: voiceCustomerId,
          petId: voicePetId,
          patientName: voicePetId === petId ? petDetails?.name : undefined,
          emrStatus: "DRAFT",
        });
        // Same event the Create EMR and PWL paths fire; no mic pre-check here
        // either, hence `mic_unchecked`. The recorder continues the funnel.
        logAutoStart("VOICE_EMR_AUTOSTART_STARTED", "Start recording", {
          emr_id: emrId,
          event_action: "mic_unchecked",
        });
      } catch (error) {
        logSkip("error", "Could not start recording", {
          source: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        });
        toast.error(
          error instanceof Error ? error.message : "Couldn't start the voice recording.",
        );
      } finally {
        PWL_VOICE_INFLIGHT.delete(key);
      }
    })();
    // Driven by the voice flag + resolved ids only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, voiceCustomerId, voicePetId]);

  const setOverDraftEMRIfNeeded = (emrList: IEMRListForUserAndPetResponseData[]) => {
    const allDrafts = emrList?.filter((emr) => emr.emr_status === "DRAFT" && emr.emr_type !== "Lab Report");

    if (allDrafts?.length >= 1) {
      // restrict user to create more drafts

      const latestDraftEMR = allDrafts[0];

      if (latestDraftEMR?.created_by === userRole) {
        dispatch(setEMROverDraft(true));
      } else {
        dispatch(setEMROverDraft(false));
      }

      const data = {
        createdAt: latestDraftEMR.created_at,
        emrId: latestDraftEMR.emr_id,
        cartId: latestDraftEMR.cart_id,
        createdBy: latestDraftEMR.created_by,
        customerDetails: latestDraftEMR.customer_profile_section,
        status: latestDraftEMR?.emr_status,
      };
      dispatch(setLatestEMRDetails(data));
      setModalData(data);
    } else {
      dispatch(setEMROverDraft(false));
    }
  };

  const handleSetDraftEMRs = (emrList: IEMRListForUserAndPetResponseData[]) => {
    const de = emrList.filter((emr) => emr.emr_status === "DRAFT");
    setDRAFTEMRs(de);
  };

  useEffect(() => {
    if (customerId && petId) {
      fetchV2EmrList(customerId, petId).then((emrList) => {
        setOverDraftEMRIfNeeded(emrList);
        handleSetDraftEMRs(emrList);
      });
    }
  }, [customerId, petId]);

  const voiceFlagRef = useRef(new URLSearchParams(window.location.search).get("voice") === "1");
  const voiceArmedRef = useRef(false);
  const [voiceEmrId, setVoiceEmrId] = useState<string | null>(null);
  const [isVoicePanelOpen, setIsVoicePanelOpen] = useState(false);

  useEffect(() => {
    if (!voiceFlagRef.current || voiceArmedRef.current || !customerId || !petId) return;
    voiceArmedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    params.delete("voice");
    const emrIdFromUrl = params.get("emr_id");
    params.delete("emr_id");
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
    );

    const armVoice = async () => {
      if (emrIdFromUrl) {
        setVoiceEmrId(emrIdFromUrl);
        setIsVoicePanelOpen(true);
        return;
      }
      const emrList = await fetchV2EmrList(customerId, petId);
      const activeEMR = Array.isArray(emrList)
        ? emrList.filter((emr) => isActiveEMRForPet(emr, petId))[0]
        : undefined;
      if (activeEMR?.emr_id) {
        setVoiceEmrId(activeEMR.emr_id);
        setIsVoicePanelOpen(true);
      }
    };

    void armVoice();
  }, [customerId, petId]);

  useEffect(() => {
    const handleFocus = () => {
      if (customerId) {
        fetchV2EmrList(customerId, petId).then((emrList) => {
          setOverDraftEMRIfNeeded(emrList);
          handleSetDraftEMRs(emrList);
        });
      }
    };

    document.addEventListener("visibilitychange", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, []);

  const handleEMRQuickView = (emrId: string) => {
    setQuickViewModalData({
      emrId: emrId,
    });
    quickViewVisible();
  };

  const handleDeleteEMR = (emr: any, cartId: string) => {
    if (emr?.version === 2) {
      deleteV2EMR(emr?.emr_id).then(() => {
        fetchV2EmrList(customerId, petId).then((emrList) => {
          setOverDraftEMRIfNeeded(emrList);
        });
      });
    } else {
      deleteEMR(emr?.emr_id, cartId).then(() => {
        fetchV2EmrList(customerId, petId).then((emrList) => {
          setOverDraftEMRIfNeeded(emrList);
        });
      });
    }
  };

  const handleViewEMR = (emrId: string, emrCustomerId: string, emrPetId: string) => {
    navigate(`${getRouteKey("EMR_PDF_VIEW", "url")}/${emrCustomerId}/${emrPetId}/${emrId}`);
  };

  const handleEditDrafEMR = (emr: any, cartId: string, isCompleted: boolean = false) => {
    if (emr?.version === 2) {
      // With a recording live, stay in this tab so the recorder is torn down here
      // and picks the same session back up on the EMR page.
      const url =
        `/app/customer/profile/create-emr?emr_id=${emr?.emr_id}&emr_status=${emr?.emr_status}` +
        `${voiceEmrId ? "&voice=1" : ""}${voiceEmrId === emr?.emr_id ? "&voice_resume=1" : ""}`;
      if (voiceEmrId) {
        navigate(url);
      } else {
        window.open(url, "_blank");
      }
    } else {
      dispatch(setEMRMode("EDIT"));
      window.open(
        `/app/customer/profile/customer-create-emr/${customerId}/${petId}/${emr?.emr_id}/${cartId}?edit=true&is_completed=${isCompleted}`,
        "_self",
      );
    }
  };

  return (
    <div className="px-2">
      {voiceEmrId && (
        <VoiceEMRRecorder
          emrId={voiceEmrId}
          onSuccess={() => {
            if (customerId && petId) {
              fetchV2EmrList(customerId, petId).then((emrList) => {
                setOverDraftEMRIfNeeded(emrList);
                handleSetDraftEMRs(emrList);
              });
            }
          }}
          isOpen={isVoicePanelOpen}
          onClose={() => setIsVoicePanelOpen(false)}
          onRestore={() => setIsVoicePanelOpen(true)}
          autoStart
        />
      )}
      <div className="">
        <EMRTabHeader
          showEMRCreateButton={true}
          voiceEmrId={voiceEmrId}
          content={
            <div className="flex justify-start items-center gap-x-1 w-full">
              {" "}
              <FaRegFilePdf /> <span>EMR PDFs</span>
            </div>
          }
          petStatus={petDetails?.status}
          customerId={customerId}
          petId={petId}
          petName={""}
        />
      </div>

      {/* Completed And Draft EMRs */}
      <MedicineHistoryTab />
      <AISummaryTab />

      <div className="w-full mt-2">
        {(() => {
          const vetbuddyItem = v2EmrListData?.find((emr: any) => "vetbuddy_emrs" in emr);
          const regularEmrs = v2EmrListData?.filter((emr: any) => !("vetbuddy_emrs" in emr)) ?? [];
          const vetbuddyEmrs: { prescription_link: string; created_at: string; file_name: string }[] =
            (vetbuddyItem as any)?.vetbuddy_emrs ?? [];

          return (
            <>
        <div className="flex flex-wrap items-center gap-2 text-theme-xs">
          <SimpleBadge
            label={`${draftEMRs.length} Drafts`}

          />
          <SimpleBadge
            colorClassName="text-green-600 bg-green-50 ring-green-500/10"
            label={`${regularEmrs.length - draftEMRs.length} Completed`}

          />
        </div>

        {isLoading ? (
          <div>
            <SkeletonType1 />
            <SkeletonType1 className="mt-2" />
          </div>
        ) : (
          regularEmrs?.length > 0 && (
            <div className="mt-2">
              <div className="mt-2 sm:mt-3 mb-10 flow-root">
                <ul role="list" className="-mb-8">
                  {regularEmrs?.map((emr, idx) => (
                    <li key={emr.emr_id}>
                      <div className="relative pb-6">
                        {idx !== v2EmrListData.length - 1 ? (
                          <span aria-hidden="true" className="absolute left-4 -translate-x-1/2 top-0 h-full w-0.5 bg-gray-200" />
                        ) : null}
                        <div className="relative flex space-x-2">
                          <div>
                            <span
                              className={clsx(
                                "flex h-8 w-8 items-center justify-center rounded-full ring-8 text-white ring-white",
                                emr?.emr_status === "DRAFT" ? "bg-yellow-300 " : "bg-green-300 ",
                              )}
                            >
                              <GrDocumentText className="w-4 h-4" />
                            </span>
                          </div>
                          <div className="flex flex-1 justify-between space-x-2 min-w-0 flex-wrap sm:flex-nowrap">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-5">
                              <p className="flex justify-start items-center gap-x-2 font-semibold text-gray-900 text-theme-xs flex-wrap sm:flex-nowrap">
                                <span className="font-semibold text-theme-xs ml-2 ">
                                  {emr.created_at ? fromUnixTime(emr.created_at)?.toDateString() : "—"}
                                </span>
                                <span>|</span>
                                <span className="flex items-center gap-x-1 text-gray-600">
                                  <span className="">Created at</span>
                                  <span className="font-semibold">
                                    {emr.created_at ? format(fromUnixTime(emr.created_at), "hh:mm a") : "—"}
                                  </span>
                                </span>
                                {/* Completed At (only when available) */}
                                {emr.emr_completion_info?.first_completed_at && (

                                  <span className="flex items-center gap-x-1 text-gray-600">
                                    <span>|</span>
                                    <span className="text-theme-xs">Completed at</span>
                                    <span className="font-semibold">
                                      {format(
                                        fromUnixTime(emr.emr_completion_info.first_completed_at),
                                        "hh:mm a"
                                      )}
                                    </span>
                                  </span>
                                )}<span>|</span>
                                <span className="font-semibold text-theme-xs text-gray-600 inline-flex items-center gap-x-2">
                                  <span>{emr.created_by?.replace("&nbsp;", " ")}</span>
                                  {emr?.emr_type === "User Medicine Prescriptions" && (
                                    <span
                                      className="inline-flex items-center gap-x-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                      style={{
                                        color: "#EC4F68",
                                        background: "linear-gradient(45deg, rgba(208, 225, 251, 0.1), rgba(236, 79, 104, 0.2))",
                                        borderColor: "#EC4F68",
                                      }}
                                    >

                                      E-Pharmacy
                                    </span>
                                  )}
                                  {emr?.emr_type === "Lab Report" && (
                                    <span
                                      className="inline-flex items-center gap-x-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                      style={{
                                        color: "#6D28D9",
                                        background: "linear-gradient(45deg, rgba(237, 233, 254, 0.3), rgba(139, 92, 246, 0.2))",
                                        borderColor: "#6D28D9",
                                      }}
                                    >
                                      Lab Interpretation
                                    </span>
                                  )}
                                </span>
                              </p>


                            </div>
                            <div className="hidden sm:flex text-right  justify-start items-center gap-x-3 text-gray-500 text-theme-xs whitespace-nowrap">
                              <p className="flex text-gray-500 text-xs leading-5">
                                {emr.emr_status === "DRAFT" ? (
                                  <SimpleBadge
                                    colorClassName="text-yellow-600 bg-yellow-50 ring-yellow-500/10"
                                    label={"DRAFT"}
                                  />
                                ) : (
                                  <SimpleBadge
                                    colorClassName="text-green-600 bg-green-50 ring-green-500/10"
                                    label={"COMPLETED"}
                                  />
                                )}
                              </p>
                              <div className="ml-2">
                                <RBACFlag actionId="EMR_ACTION_BUTTON">
                                  {" "}
                                  <EMRMenu
                                    customerId={customerId}
                                    emr={emr}
                                    handleDeleteEMR={handleDeleteEMR}
                                    handleEMRQuickView={handleEMRQuickView}
                                    handleEditDrafEMR={handleEditDrafEMR}
                                    handleViewEMR={handleViewEMR}
                                    petId={petId}
                                  />
                                </RBACFlag>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className=" sm:hidden text-right flex justify-start float-end items-center gap-x-3 text-gray-500 text-theme-xs whitespace-nowrap">
                          <p className="flex text-gray-500 text-theme-xs ">
                            {emr.emr_status === "DRAFT" ? (
                              <SimpleBadge
                                colorClassName="text-yellow-600 bg-yellow-50 ring-yellow-500/10 text-theme-xs"
                                label={"DRAFT"}
                              />
                            ) : (
                              <SimpleBadge
                                colorClassName="text-green-600 bg-green-50 ring-green-500/10 text-theme-xs"
                                label={"COMPLETED"}
                              />
                            )}
                          </p>
                          <div className="ml-2">
                            <RBACFlag actionId="EMR_ACTION_BUTTON">
                              {" "}
                              <EMRMenu
                                customerId={customerId}
                                emr={emr}
                                handleDeleteEMR={handleDeleteEMR}
                                handleEMRQuickView={handleEMRQuickView}
                                handleEditDrafEMR={handleEditDrafEMR}
                                handleViewEMR={handleViewEMR}
                                petId={petId}
                              />
                            </RBACFlag>
                          </div>
                        </div>
                        {emr?.version === 2 ? (
                          <EMRList emr={emr} />
                        ) : (
                          <EMRTimelineRenderer
                            key={`${emr.emr_id}:${idx}`}
                            emr={{
                              ...emr,
                            }}
                          />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        )}

        {/* VetBuddy EMR Records */}
        {!isLoading && vetbuddyEmrs.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-x-2 mb-3">
              <FaRegFilePdf className="text-gray-500" />
              <span className="font-semibold text-gray-700 text-theme-xs">VetBuddy EMR History</span>
              <SimpleBadge label={`${vetbuddyEmrs.length} Records`} />
            </div>
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-100 text-theme-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600">File Name</th>
                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vetbuddyEmrs.map((record, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5">
                        {record?.prescription_link ? (
                          <a
                            href={record.prescription_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-x-1.5 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            <FaRegFilePdf className="shrink-0" />
                            <span>{record?.file_name ?? "—"}</span>
                          </a>
                        ) : (
                          <span className="flex items-center gap-x-1.5 text-gray-400">
                            <FaRegFilePdf className="shrink-0" />
                            <span>{record?.file_name ?? "—"}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{record?.created_at ? veticDateFormat(record.created_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default EMRTab;
